import React from 'react';
import {
  EMPTY_WORKSPACE_NAVIGATION_SETTINGS,
  UpdateWorkspaceNavigationSettingsRequest,
  WorkspaceNavigationSettings,
  workspaceNavigationService,
} from '../services/workspaceNavigationService';

/**
 * One shared copy of the workspace navigation settings per tenant.
 *
 * The sidebar and the admin page both read from here, so an admin's save shows up in their own
 * sidebar at once without a reload. The store lives outside React to keep wiring trivial (no
 * provider to thread through the app shell or its tests).
 */
type StoreState = {
  tenantId: string | null;
  settings: WorkspaceNavigationSettings;
  loaded: boolean;
};

let state: StoreState = { tenantId: null, settings: EMPTY_WORKSPACE_NAVIGATION_SETTINGS, loaded: false };
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of Array.from(listeners)) {
    listener();
  }
};

const setState = (next: StoreState) => {
  state = next;
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => state;

const load = (tenantId: string): Promise<void> => {
  if (state.tenantId !== tenantId) {
    setState({ tenantId, settings: EMPTY_WORKSPACE_NAVIGATION_SETTINGS, loaded: false });
    inflight = null;
  }

  if (!inflight) {
    inflight = workspaceNavigationService.get()
      .then(settings => {
        if (state.tenantId === tenantId) {
          setState({ tenantId, settings, loaded: true });
        }
      })
      .catch(() => {
        if (state.tenantId === tenantId) {
          setState({ ...state, loaded: true });
        }
      })
      .finally(() => {
        inflight = null;
      });
  }

  return inflight;
};

/** Test helper: forget everything cached. */
export const resetWorkspaceNavigationSettingsStore = () => {
  state = { tenantId: null, settings: EMPTY_WORKSPACE_NAVIGATION_SETTINGS, loaded: false };
  inflight = null;
};

type UseWorkspaceNavigationSettingsResult = {
  settings: WorkspaceNavigationSettings;
  loaded: boolean;
  refresh: () => Promise<void>;
  save: (request: UpdateWorkspaceNavigationSettingsRequest) => Promise<WorkspaceNavigationSettings>;
};

export const useWorkspaceNavigationSettings = (tenantId: string | null): UseWorkspaceNavigationSettingsResult => {
  const snapshot = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  React.useEffect(() => {
    if (!tenantId) {
      return;
    }

    if (snapshot.tenantId !== tenantId || (!snapshot.loaded && !inflight)) {
      void load(tenantId);
    }
  }, [snapshot.loaded, snapshot.tenantId, tenantId]);

  const refresh = React.useCallback(async () => {
    if (tenantId) {
      inflight = null;
      await load(tenantId);
    }
  }, [tenantId]);

  const save = React.useCallback(async (request: UpdateWorkspaceNavigationSettingsRequest) => {
    const settings = await workspaceNavigationService.update(request);
    setState({ tenantId, settings, loaded: true });
    return settings;
  }, [tenantId]);

  const matchesTenant = snapshot.tenantId === tenantId;

  return {
    settings: matchesTenant ? snapshot.settings : EMPTY_WORKSPACE_NAVIGATION_SETTINGS,
    loaded: matchesTenant && snapshot.loaded,
    refresh,
    save,
  };
};
